import { FormattedNumber } from 'react-intl';

import { NumberFormatOptions, NumberFormatOptionsStyle } from '@formatjs/ecma402-abstract';

export interface NumeralProps extends Omit<NumberFormatOptions, 'style'> {
  value?: number;
  /**
   * if not set, then numbers will be shown as-is.
   * - exception is that xyz.0 will be floored to xyz.
   */
  showDecimal?: boolean;

  /**
   * if true, 1000 -> 1k
   */
  isCompact?: boolean;

  /**
   * if true, format number as currency, else decimal
   */
  isCurrency?: boolean;

  // Turns off normalization on values
  shouldNormalizeValue?: boolean;

  /**
   * a reference to NumberFormatOption's "style" parameter, which linting does not like
   * 'decimal' | 'percent' | 'currency' | 'unit'
   */
  type?: NumberFormatOptionsStyle;
}

const normalizeValue = (props: NumeralProps): number => {
  const { value = 0, type } = props;

  if (type === 'percent') {
    return value / 100.0;
  }

  return value;
};

const isNumberWhole = (props: NumeralProps): boolean => {
  const { value, type } = props;

  const rawValue: number = value != null ? value : 0;

  return (type === 'percent' ? rawValue * 100 : rawValue) % 1 === 0;
};

export const Numeral = (props: NumeralProps) => {
  const {
    currency,
    value = 0,
    isCompact = false,
    isCurrency = false,
    shouldNormalizeValue = true,
    showDecimal = false,
    type,
    ...numeralProps
  } = props;

  const normalizedValue = shouldNormalizeValue ? normalizeValue(props) : value;
  const isWholeNumber = isNumberWhole(props);
  const notation = isCompact ? 'compact' : undefined;

  const maximumFractionDigits = isCompact ? 1 : 2;
  const minimumFractionDigits = !isWholeNumber || showDecimal ? maximumFractionDigits : 0;

  const currencyProps = isCurrency
    ? {
        style: 'currency' as NumberFormatOptionsStyle,
        currency: currency || 'USD',
      }
    : {};

  return (
    <FormattedNumber
      value={normalizedValue}
      style={type}
      notation={notation}
      minimumFractionDigits={minimumFractionDigits}
      maximumFractionDigits={maximumFractionDigits}
      {...numeralProps}
      {...currencyProps}
    />
  );
};
